import {
  recordMonitoringMetricSample,
  type MonitoringMetricSample,
  type PodMonitoringSchema,
} from "..";
import { getTrackableTarget } from "./dom";
import type {
  InteractionMonitoringOptions,
  InteractionResponseKind,
  PendingInteractionSession,
} from "./types";

// Browser interaction monitoring that watches clicks, responses, and redirects.
const DEFAULT_RESPONSE_WINDOW_MS = 1500;
const DEFAULT_RAGE_CLICK_WINDOW_MS = 2000;
const DEFAULT_RAGE_CLICK_THRESHOLD = 3;

const CLICK_RESPONSE_TIME_METRIC_ID = "ux.click_response_time";
const REDIRECTION_SUCCESS_METRIC_ID = "ux.redirection_success";
const DEAD_CLICKS_METRIC_ID = "ux.dead_clicks";
const RAGE_CLICKS_METRIC_ID = "ux.rage_clicks";

// Stores patched browser APIs so multiple installers can share one set of hooks.
interface InteractionPatchGlobals {
  __bflInteractionOriginalPushState__?: History["pushState"];
  __bflInteractionOriginalReplaceState__?: History["replaceState"];
  __bflInteractionOriginalWindowOpen__?: Window["open"];
  __bflInteractionPatchRefCount__?: number;
}

// Collects the shared click target attributes added to every interaction sample.
function createCommonAttributes(
  session: PendingInteractionSession
): Record<string, string | number | boolean> {
  return {
    target_signature: session.target.signature,
    target_tag: session.target.tagName,
    target_role: session.target.role,
    target_label: session.target.label,
    target_selector: session.target.selector,
    target_href: session.target.href,
    target_path: session.target.path,
    click_count: session.clickCount,
  };
}

// Thin wrapper so the runtime only talks to one metric-recording entry point.
function recordMetric(
  schema: PodMonitoringSchema,
  metricId: string,
  sample: MonitoringMetricSample
): void {
  recordMonitoringMetricSample(schema, metricId, sample);
}

// Patches history and window.open so navigation responses can be detected.
function installHistoryAndWindowOpenPatches(
  onNavigationSignal: (kind: InteractionResponseKind) => void
): () => void {
  const globals = window as Window & InteractionPatchGlobals;

  if (!globals.__bflInteractionOriginalPushState__) {
    globals.__bflInteractionOriginalPushState__ = window.history.pushState.bind(window.history);
    globals.__bflInteractionOriginalReplaceState__ = window.history.replaceState.bind(
      window.history
    );
    globals.__bflInteractionOriginalWindowOpen__ = window.open.bind(window);
    globals.__bflInteractionPatchRefCount__ = 0;
  }

  globals.__bflInteractionPatchRefCount__ = (globals.__bflInteractionPatchRefCount__ ?? 0) + 1;

  window.history.pushState = function pushState(...args) {
    const result = globals.__bflInteractionOriginalPushState__?.(...args);
    onNavigationSignal("history");
    return result;
  };

  window.history.replaceState = function replaceState(...args) {
    const result = globals.__bflInteractionOriginalReplaceState__?.(...args);
    onNavigationSignal("history");
    return result;
  };

  window.open = function patchedWindowOpen(...args) {
    const result = globals.__bflInteractionOriginalWindowOpen__?.(...args) ?? null;
    onNavigationSignal("window_open");
    return result;
  };

  return () => {
    const nextCount = (globals.__bflInteractionPatchRefCount__ ?? 1) - 1;
    globals.__bflInteractionPatchRefCount__ = Math.max(nextCount, 0);

    if (globals.__bflInteractionPatchRefCount__ === 0) {
      if (globals.__bflInteractionOriginalPushState__) {
        window.history.pushState = globals.__bflInteractionOriginalPushState__;
      }
      if (globals.__bflInteractionOriginalReplaceState__) {
        window.history.replaceState = globals.__bflInteractionOriginalReplaceState__;
      }
      if (globals.__bflInteractionOriginalWindowOpen__) {
        window.open = globals.__bflInteractionOriginalWindowOpen__;
      }

      delete globals.__bflInteractionOriginalPushState__;
      delete globals.__bflInteractionOriginalReplaceState__;
      delete globals.__bflInteractionOriginalWindowOpen__;
      delete globals.__bflInteractionPatchRefCount__;
    }
  };
}

// Tracks clicks, UI responses, and redirects to surface dead and rage clicks.
export function installInteractionMonitoring(options: InteractionMonitoringOptions): () => void {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return () => undefined;
  }

  const responseWindowMs = options.responseWindowMs ?? DEFAULT_RESPONSE_WINDOW_MS;
  const rageClickWindowMs = options.rageClickWindowMs ?? DEFAULT_RAGE_CLICK_WINDOW_MS;
  const rageClickThreshold = options.rageClickThreshold ?? DEFAULT_RAGE_CLICK_THRESHOLD;
  const sessions = new Map<string, PendingInteractionSession>();
  let sessionCounter = 0;

  // Removes a session and clears its timeout once it is finished.
  const clearSession = (session: PendingInteractionSession) => {
    if (session.timeoutId) {
      clearTimeout(session.timeoutId);
      session.timeoutId = null;
    }
    sessions.delete(session.target.signature);
  };

  // Uses the latest click as the best candidate for incoming response signals.
  const getMostRecentPendingSession = (): PendingInteractionSession | null => {
    let candidate: PendingInteractionSession | null = null;

    for (const session of sessions.values()) {
      if (!candidate || session.lastClickedAt > candidate.lastClickedAt) {
        candidate = session;
      }
    }

    return candidate;
  };

  // Records how long it took for a click to trigger any detectable response.
  const recordClickResponseTime = (
    schema: PodMonitoringSchema,
    session: PendingInteractionSession,
    kind: InteractionResponseKind
  ) => {
    if (session.responseRecorded) {
      return;
    }

    session.responseRecorded = true;

    recordMetric(schema, CLICK_RESPONSE_TIME_METRIC_ID, {
      value: performance.now() - session.startedAt,
      unit: "ms",
      success: true,
      extra: {
        ...createCommonAttributes(session),
        response_kind: kind,
      },
    });
  };

  // Records whether a navigation candidate eventually redirected successfully.
  const recordRedirectSuccess = (
    schema: PodMonitoringSchema,
    session: PendingInteractionSession,
    redirectSuccess: boolean,
    kind: InteractionResponseKind | "timeout"
  ) => {
    if (!session.target.navigationCandidate || session.redirectRecorded) {
      return;
    }

    session.redirectRecorded = true;

    recordMetric(schema, REDIRECTION_SUCCESS_METRIC_ID, {
      value: 1,
      unit: "attempt",
      success: redirectSuccess,
      extra: {
        ...createCommonAttributes(session),
        redirect_success: redirectSuccess,
        response_kind: kind,
      },
    });
  };

  // Marks clicks with no detected response as dead clicks.
  const recordDeadClick = (schema: PodMonitoringSchema, session: PendingInteractionSession) => {
    recordMetric(schema, DEAD_CLICKS_METRIC_ID, {
      value: 1,
      unit: "count",
      success: false,
      extra: {
        ...createCommonAttributes(session),
        dead_click: true,
      },
    });
  };

  // Finalizes a session when no response arrived within the configured window.
  const handleTimeout = (schema: PodMonitoringSchema, signature: string) => {
    const session = sessions.get(signature);

    if (!session) {
      return;
    }

    if (!session.responseRecorded) {
      recordDeadClick(schema, session);
    }

    recordRedirectSuccess(schema, session, false, "timeout");
    clearSession(session);
  };

  // Resets the response timer each time a tracked target is clicked again.
  const scheduleTimeout = (schema: PodMonitoringSchema, session: PendingInteractionSession) => {
    if (session.timeoutId) {
      clearTimeout(session.timeoutId);
    }

    session.timeoutId = setTimeout(
      () => handleTimeout(schema, session.target.signature),
      responseWindowMs
    );
  };

  // Applies DOM or navigation signals to the most recent pending click.
  const handleResponseSignal = (kind: InteractionResponseKind) => {
    const session = getMostRecentPendingSession();

    if (!session) {
      return;
    }

    recordClickResponseTime(options.schema, session, kind);

    const isNavigationSignal =
      kind === "history" || kind === "hashchange" || kind === "pagehide" || kind === "window_open";

    if (isNavigationSignal) {
      recordRedirectSuccess(options.schema, session, true, kind);
      clearSession(session);
      return;
    }

    if (!session.target.navigationCandidate) {
      clearSession(session);
    }
  };

  // Starts or updates an interaction session for each monitored click.
  const onDocumentClick = (event: MouseEvent) => {
    const target = getTrackableTarget(event.target);

    if (!target) {
      return;
    }

    const now = performance.now();
    const existingSession = sessions.get(target.signature);

    if (existingSession && now - existingSession.lastClickedAt <= responseWindowMs) {
      existingSession.clickCount += 1;
      existingSession.lastClickedAt = now;

      if (
        !existingSession.rageRecorded &&
        existingSession.clickCount >= rageClickThreshold &&
        now - existingSession.startedAt <= rageClickWindowMs
      ) {
        existingSession.rageRecorded = true;
        recordMetric(options.schema, RAGE_CLICKS_METRIC_ID, {
          value: 1,
          unit: "count",
          success: false,
          extra: {
            ...createCommonAttributes(existingSession),
            rage_click: true,
          },
        });
      }

      scheduleTimeout(options.schema, existingSession);
      return;
    }

    const session: PendingInteractionSession = {
      id: ++sessionCounter,
      startedAt: now,
      lastClickedAt: now,
      clickCount: 1,
      responseRecorded: false,
      redirectRecorded: false,
      rageRecorded: false,
      timeoutId: null,
      target,
    };

    sessions.set(target.signature, session);
    scheduleTimeout(options.schema, session);
  };

  const mutationObserver = new MutationObserver((mutations) => {
    if (mutations.length === 0) {
      return;
    }

    handleResponseSignal("mutation");
  });

  // Treats URL hash updates as a response to the latest click.
  const onHashChange = () => handleResponseSignal("hashchange");
  // Treats page exits as a redirect-like response to the latest click.
  const onPageHide = () => handleResponseSignal("pagehide");
  const removePatches = installHistoryAndWindowOpenPatches(handleResponseSignal);

  document.addEventListener("click", onDocumentClick, true);
  window.addEventListener("hashchange", onHashChange, true);
  window.addEventListener("pagehide", onPageHide, true);

  if (document.body) {
    mutationObserver.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      characterData: true,
    });
  }

  return () => {
    document.removeEventListener("click", onDocumentClick, true);
    window.removeEventListener("hashchange", onHashChange, true);
    window.removeEventListener("pagehide", onPageHide, true);
    mutationObserver.disconnect();
    removePatches();

    for (const session of sessions.values()) {
      if (session.timeoutId) {
        clearTimeout(session.timeoutId);
      }
    }

    sessions.clear();
  };
}
